import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle,
  Sparkles, Globe, Building2, ArrowRight, ArrowLeft, Database,
} from "lucide-react";

const FIELD_LABELS = {
  name: "Business Name",
  phone: "Phone",
  email: "Email",
  website: "Website",
  city: "City",
  state: "State",
  address: "Address",
  niche: "Niche / Industry",
  notes: "Notes",
};

export default function Import() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // step 1: file → preview
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  // step 2: configure import
  const [campaigns, setCampaigns] = useState([]);
  const [campaignMode, setCampaignMode] = useState("existing"); // "existing" | "new"
  const [campaignId, setCampaignId] = useState("");
  const [newCampaignName, setNewCampaignName] = useState("");
  const [newCampaignNiche, setNewCampaignNiche] = useState("");
  const [newCampaignCity, setNewCampaignCity] = useState("");
  const [dedupMode, setDedupMode] = useState("skip");
  const [enrichWeb, setEnrichWeb] = useState(true);
  const [enrichApollo, setEnrichApollo] = useState(false);
  const [enrichAi, setEnrichAi] = useState(true);
  const [forceSequence, setForceSequence] = useState(false);

  // step 3: result
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/campaigns");
        setCampaigns(data || []);
        if (data && data.length > 0) {
          setCampaignId(data[0].id);
        } else {
          setCampaignMode("new");
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  const handleFile = async (selected) => {
    if (!selected) return;
    if (!/\.csv$/i.test(selected.name) && selected.type !== "text/csv") {
      toast.error("Please pick a .csv file");
      return;
    }
    if (selected.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5MB)");
      return;
    }
    setFile(selected);
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", selected);
      const { data } = await api.post("/leads/import-csv/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      toast.success(`Parsed ${data.total_rows} rows`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPreviewing(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submitCommit = async () => {
    if (!preview?.preview?.length) return;
    if (campaignMode === "existing" && !campaignId) {
      toast.error("Select a campaign or create a new one");
      return;
    }
    if (campaignMode === "new" && newCampaignName.trim().length < 2) {
      toast.error("New campaign name must be at least 2 characters");
      return;
    }

    setCommitting(true);
    try {
      // Preview only returns the first 50 rows, so re-parse the full file
      // client-side using the same alias map the backend uses.
      const allRows = await parseCsvClient(file);

      const body = {
        rows: allRows,
        dedup_mode: dedupMode,
        force_sequence: forceSequence,
        enrichment: {
          web: enrichWeb,
          apollo: enrichApollo,
          ai_classify: enrichAi,
        },
      };
      if (campaignMode === "existing") {
        body.campaign_id = campaignId;
      } else {
        body.new_campaign_name = newCampaignName.trim();
        if (newCampaignNiche.trim()) body.new_campaign_niche = newCampaignNiche.trim();
        if (newCampaignCity.trim()) body.new_campaign_city = newCampaignCity.trim();
      }

      const { data } = await api.post("/leads/import-csv/commit", body);
      setResult(data);
      toast.success(
        `Imported ${data.inserted_count} new + ${data.updated_count} updated`
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="px-8 py-10 max-w-6xl" data-testid="import-page">
      <header className="mb-10">
        <div className="overline mb-2 text-[#002FA7]">Bulk Import</div>
        <h1 className="font-heading text-4xl font-black tracking-tight mb-2">
          Import leads from CSV
        </h1>
        <p className="text-zinc-600 max-w-2xl">
          Upload a CSV with your leads. We'll auto-detect columns, dedupe against your
          workspace, and (optionally) enrich each lead with website scraping, Apollo
          decision-makers, and Claude AI scoring — all in parallel.
        </p>
      </header>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-8 text-xs uppercase tracking-wider font-semibold">
        <StepDot active={!preview} done={!!preview} label="1 · Upload" />
        <div className="h-px w-8 bg-zinc-300" />
        <StepDot active={!!preview && !result} done={!!result} label="2 · Configure" />
        <div className="h-px w-8 bg-zinc-300" />
        <StepDot active={!!result} done={false} label="3 · Done" />
      </div>

      {/* STEP 1: Upload */}
      {!preview && !result && (
        <div className="border-2 border-dashed border-zinc-300 p-12 text-center bg-zinc-50" data-testid="import-dropzone">
          <FileSpreadsheet className="w-16 h-16 mx-auto text-zinc-400 mb-4" strokeWidth={1.2} />
          <h2 className="font-heading text-2xl font-bold mb-2">Drop a CSV file</h2>
          <p className="text-zinc-600 mb-6 text-sm">
            Supported columns: <span className="font-mono">name, phone, email, website, city, state, address, niche</span>
            <br />Up to 5MB / 5,000 rows
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="import-file-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={previewing}
            data-testid="import-pick-file-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#002FA7] text-white font-semibold uppercase tracking-wider text-xs hover:bg-[#001f7f] transition-colors disabled:opacity-50"
          >
            {previewing ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</>
            ) : (
              <><Upload className="w-4 h-4" /> Choose CSV</>
            )}
          </button>
        </div>
      )}

      {/* STEP 2: Configure */}
      {preview && !result && (
        <div className="space-y-8" data-testid="import-configure">
          {/* Detection summary */}
          <div className="border border-zinc-200 p-6 bg-white">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-sm font-semibold text-zinc-900">
                  <FileSpreadsheet className="w-4 h-4 inline mr-2" />
                  {file?.name}
                </div>
                <div className="text-xs text-zinc-500 font-mono mt-1">
                  {preview.total_rows} usable rows · {preview.headers.length} columns
                </div>
              </div>
              <button
                onClick={reset}
                data-testid="import-change-file-btn"
                className="text-xs text-zinc-600 hover:text-zinc-900 underline"
              >
                Change file
              </button>
            </div>
            {/* mapping pills */}
            <div className="flex flex-wrap gap-2 mb-2">
              {Object.entries(preview.detected_mapping).map(([h, mapped]) => (
                <span
                  key={h}
                  className={`text-xs px-2 py-1 font-mono ${
                    mapped
                      ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                      : "bg-zinc-100 text-zinc-500 border border-zinc-200 line-through"
                  }`}
                  title={mapped ? `→ ${FIELD_LABELS[mapped]}` : "unmapped — will be ignored"}
                >
                  {h}{mapped ? ` → ${mapped}` : ""}
                </span>
              ))}
            </div>
            {preview.unmapped_columns.length > 0 && (
              <div className="text-xs text-amber-700 mt-2">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                {preview.unmapped_columns.length} column(s) won't be imported.
              </div>
            )}
          </div>

          {/* Preview table */}
          <div className="border border-zinc-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  {Object.values(preview.detected_mapping)
                    .filter(Boolean)
                    .filter((v, i, a) => a.indexOf(v) === i)
                    .map((f) => (
                      <th key={f} className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider text-zinc-700">
                        {FIELD_LABELS[f] || f}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {preview.preview.slice(0, 10).map((row, i) => (
                  <tr key={i} className="border-b border-zinc-100">
                    {Object.values(preview.detected_mapping)
                      .filter(Boolean)
                      .filter((v, i, a) => a.indexOf(v) === i)
                      .map((f) => (
                        <td key={f} className="px-3 py-2 text-zinc-800 truncate max-w-[180px]">
                          {row[f] || <span className="text-zinc-300">—</span>}
                        </td>
                      ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.total_rows > 10 && (
              <div className="bg-zinc-50 px-3 py-2 text-xs text-zinc-500 border-t border-zinc-200">
                Showing first 10 of {preview.total_rows} rows
              </div>
            )}
          </div>

          {/* Campaign picker */}
          <div className="border border-zinc-200 p-6 bg-white">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider mb-4">
              Target campaign
            </h3>
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setCampaignMode("existing")}
                data-testid="import-campaign-existing-btn"
                disabled={campaigns.length === 0}
                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                  campaignMode === "existing"
                    ? "bg-zinc-900 text-white"
                    : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                Existing
              </button>
              <button
                onClick={() => setCampaignMode("new")}
                data-testid="import-campaign-new-btn"
                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                  campaignMode === "new"
                    ? "bg-zinc-900 text-white"
                    : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                }`}
              >
                Create new
              </button>
            </div>
            {campaignMode === "existing" ? (
              <select
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
                data-testid="import-campaign-select"
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
              >
                {campaigns.length === 0 ? (
                  <option value="">— No campaigns yet —</option>
                ) : (
                  campaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} {c.target_niche ? `· ${c.target_niche}` : ""} {c.target_city ? `· ${c.target_city}` : ""}
                    </option>
                  ))
                )}
              </select>
            ) : (
              <div className="grid sm:grid-cols-3 gap-3">
                <input
                  placeholder="Campaign name *"
                  value={newCampaignName}
                  onChange={(e) => setNewCampaignName(e.target.value)}
                  data-testid="import-new-campaign-name"
                  className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
                />
                <input
                  placeholder="Niche (e.g. venues)"
                  value={newCampaignNiche}
                  onChange={(e) => setNewCampaignNiche(e.target.value)}
                  data-testid="import-new-campaign-niche"
                  className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
                />
                <input
                  placeholder="Target city (optional)"
                  value={newCampaignCity}
                  onChange={(e) => setNewCampaignCity(e.target.value)}
                  data-testid="import-new-campaign-city"
                  className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
                />
              </div>
            )}
          </div>

          {/* Dedup mode */}
          <div className="border border-zinc-200 p-6 bg-white">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider mb-1">
              Duplicates
            </h3>
            <p className="text-xs text-zinc-500 mb-4">
              Matched by phone (last 10 digits), website host, or name+city.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <DedupOption
                value="skip"
                current={dedupMode}
                onChange={setDedupMode}
                title="Skip duplicates"
                desc="Keep existing leads untouched. Only insert truly new rows."
                testid="import-dedup-skip"
              />
              <DedupOption
                value="update"
                current={dedupMode}
                onChange={setDedupMode}
                title="Update existing"
                desc="Overwrite phone/email/website/etc on existing leads with new CSV values."
                testid="import-dedup-update"
              />
            </div>
          </div>

          {/* Enrichment */}
          <div className="border border-zinc-200 p-6 bg-white">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider mb-1">
              AI Enrichment <Sparkles className="w-3.5 h-3.5 inline ml-1 text-[#002FA7]" />
            </h3>
            <p className="text-xs text-zinc-500 mb-4">
              Run after import in the background. You can keep working — enrichment will
              fill in as it completes.
            </p>
            <div className="space-y-3">
              <EnrichOption
                checked={enrichWeb}
                onChange={setEnrichWeb}
                Icon={Globe}
                title="Website enrichment"
                desc="Scrape each website to extract email + social handles + quality score."
                testid="import-enrich-web"
              />
              <EnrichOption
                checked={enrichApollo}
                onChange={setEnrichApollo}
                Icon={Building2}
                title="Apollo decision-maker"
                desc="Look up the top decision-maker (owner / C-suite / director) per company. Uses Apollo credits."
                testid="import-enrich-apollo"
              />
              <EnrichOption
                checked={enrichAi}
                onChange={setEnrichAi}
                Icon={Sparkles}
                title="Claude ICP score + outreach copy"
                desc="Score each lead's intent and pre-draft call script + SMS + email."
                testid="import-enrich-ai"
              />
            </div>
            <label className="flex items-center gap-2 mt-5 pt-5 border-t border-zinc-200 text-sm" data-testid="import-force-sequence-wrap">
              <input
                type="checkbox"
                checked={forceSequence}
                onChange={(e) => setForceSequence(e.target.checked)}
                data-testid="import-force-sequence"
                className="w-4 h-4 accent-[#002FA7]"
              />
              <span className="text-zinc-700">
                Start outreach sequence immediately for leads with a phone number
              </span>
            </label>
          </div>

          {/* Action bar */}
          <div className="flex justify-between items-center pt-2">
            <button
              onClick={reset}
              data-testid="import-back-btn"
              className="flex items-center gap-2 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button
              onClick={submitCommit}
              disabled={committing}
              data-testid="import-commit-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#002FA7] text-white font-semibold uppercase tracking-wider text-xs hover:bg-[#001f7f] transition-colors disabled:opacity-50"
            >
              {committing ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Importing {preview.total_rows} rows…</>
              ) : (
                <>Import {preview.total_rows} rows <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Result */}
      {result && (
        <div className="space-y-6" data-testid="import-result">
          <div className="border border-emerald-200 bg-emerald-50 p-8">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mb-3" strokeWidth={1.5} />
            <h2 className="font-heading text-2xl font-bold mb-2">Import complete</h2>
            <p className="text-zinc-700 text-sm">
              {result.inserted_count > 0 && <span className="font-semibold">{result.inserted_count} new leads created. </span>}
              {result.updated_count > 0 && <span className="font-semibold">{result.updated_count} existing leads updated. </span>}
              {result.skipped_count > 0 && <span>{result.skipped_count} skipped (duplicates).</span>}
            </p>
          </div>

          <div className="grid sm:grid-cols-4 gap-4">
            <StatBox label="Inserted" value={result.inserted_count} accent="emerald" testid="import-stat-inserted" />
            <StatBox label="Updated" value={result.updated_count} accent="blue" testid="import-stat-updated" />
            <StatBox label="Skipped" value={result.skipped_count} accent="zinc" testid="import-stat-skipped" />
            <StatBox
              label="Enriching"
              value={result.enrichment_target_count}
              accent="violet"
              testid="import-stat-enriching"
            />
          </div>

          {result.enrichment_scheduled && (
            <div className="border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700 flex items-start gap-2">
              <Sparkles className="w-4 h-4 mt-0.5 text-[#002FA7]" />
              <div>
                <div className="font-semibold mb-1">Background enrichment running…</div>
                <div className="text-xs text-zinc-600">
                  Web scraping, Apollo lookups, and AI scoring are being applied to your
                  {" "}{result.enrichment_target_count} leads. Check{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/leads")}
                    className="underline font-semibold hover:text-[#002FA7]"
                    data-testid="import-go-leads-link"
                  >
                    /leads
                  </button>{" "}
                  in 1–2 minutes to see scores + drafted copy fill in.
                </div>
              </div>
            </div>
          )}

          {result.skipped?.length > 0 && (
            <details className="border border-zinc-200 bg-white p-4">
              <summary className="cursor-pointer text-sm font-semibold text-zinc-700" data-testid="import-skipped-details">
                Show {result.skipped.length} skipped rows
              </summary>
              <ul className="mt-3 space-y-1 text-xs text-zinc-600 max-h-60 overflow-y-auto font-mono">
                {result.skipped.map((s, i) => (
                  <li key={i}>{s.name} <span className="text-zinc-400">— {s.reason}</span></li>
                ))}
              </ul>
            </details>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => { reset(); }}
              data-testid="import-new-batch-btn"
              className="inline-flex items-center gap-2 px-5 py-3 border border-zinc-300 font-semibold uppercase tracking-wider text-xs hover:bg-zinc-100 transition-colors"
            >
              <Upload className="w-4 h-4" /> Import another file
            </button>
            <button
              onClick={() => navigate("/leads")}
              data-testid="import-view-leads-btn"
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#002FA7] text-white font-semibold uppercase tracking-wider text-xs hover:bg-[#001f7f] transition-colors"
            >
              <Database className="w-4 h-4" /> View imported leads
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepDot({ active, done, label }) {
  return (
    <span
      className={`px-3 py-1 ${
        done
          ? "bg-emerald-100 text-emerald-700"
          : active
          ? "bg-zinc-900 text-white"
          : "bg-zinc-100 text-zinc-500"
      }`}
    >
      {label}
    </span>
  );
}

function DedupOption({ value, current, onChange, title, desc, testid }) {
  const selected = value === current;
  return (
    <button
      type="button"
      onClick={() => onChange(value)}
      data-testid={testid}
      className={`text-left p-4 border transition-colors ${
        selected ? "border-[#002FA7] bg-[#002FA7]/5" : "border-zinc-200 hover:border-zinc-400"
      }`}
    >
      <div className="flex items-center gap-2 font-semibold text-sm mb-1">
        <span
          className={`w-3.5 h-3.5 inline-block border-2 rounded-full ${
            selected ? "border-[#002FA7] bg-[#002FA7]" : "border-zinc-400"
          }`}
        />
        {title}
      </div>
      <p className="text-xs text-zinc-600 ml-5">{desc}</p>
    </button>
  );
}

function EnrichOption({ checked, onChange, Icon, title, desc, testid }) {
  return (
    <label className="flex items-start gap-3 p-3 hover:bg-zinc-50 cursor-pointer border border-transparent hover:border-zinc-200 transition-colors">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="mt-1 w-4 h-4 accent-[#002FA7]"
      />
      <Icon className="w-4 h-4 mt-0.5 text-zinc-600" />
      <div className="flex-1">
        <div className="text-sm font-semibold text-zinc-900">{title}</div>
        <div className="text-xs text-zinc-600">{desc}</div>
      </div>
    </label>
  );
}

function StatBox({ label, value, accent, testid }) {
  const colors = {
    emerald: "border-emerald-200 bg-emerald-50",
    blue: "border-blue-200 bg-blue-50",
    zinc: "border-zinc-200 bg-zinc-50",
    violet: "border-violet-200 bg-violet-50",
  };
  return (
    <div className={`border ${colors[accent]} p-4`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider font-semibold text-zinc-600 mb-1">
        {label}
      </div>
      <div className="text-3xl font-heading font-black">{value}</div>
    </div>
  );
}

// Lightweight client-side CSV parser used to send ALL rows on commit, since
// the /preview endpoint caps its response at 50 rows. Mirrors the backend's
// header aliasing logic so the commit payload matches what the server expects.
const ALIAS_MAP = {
  name: ["name", "business", "company", "business name", "company name", "lead", "lead name"],
  email: ["email", "e-mail", "mail", "correo"],
  phone: ["phone", "phone number", "tel", "telephone", "telefono", "teléfono", "mobile", "movil", "móvil"],
  website: ["website", "url", "site", "web", "domain", "homepage"],
  city: ["city", "ciudad", "town", "locality"],
  state: ["state", "region", "provincia", "province"],
  address: ["address", "street", "direccion", "dirección", "addr"],
  niche: ["niche", "industry", "category", "sector", "vertical"],
  notes: ["notes", "comments", "description", "note", "comment"],
};

function mapHeader(raw) {
  const h = (raw || "").trim().toLowerCase();
  if (!h) return null;
  for (const [canon, aliases] of Object.entries(ALIAS_MAP)) {
    if (aliases.includes(h)) return canon;
  }
  return null;
}

async function parseCsvClient(file) {
  const text = await file.text();
  // Sniff delimiter — pick whichever appears most in the header line.
  const firstLine = text.split(/\r?\n/, 1)[0] || "";
  const counts = { ",": 0, ";": 0, "\t": 0, "|": 0 };
  for (const ch of firstLine) if (ch in counts) counts[ch] += 1;
  const delim = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0] || ",";

  const rows = [];
  let i = 0;
  const cells = [];
  let cell = "";
  let inQuotes = false;
  const flushCell = () => { cells.push(cell); cell = ""; };
  const flushRow = () => { rows.push(cells.slice()); cells.length = 0; };

  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 2; continue; }
        inQuotes = false; i += 1; continue;
      }
      cell += c; i += 1; continue;
    }
    if (c === '"') { inQuotes = true; i += 1; continue; }
    if (c === delim) { flushCell(); i += 1; continue; }
    if (c === "\r") { i += 1; continue; }
    if (c === "\n") { flushCell(); flushRow(); i += 1; continue; }
    cell += c; i += 1;
  }
  if (cell.length || cells.length) { flushCell(); flushRow(); }

  if (rows.length < 2) return [];
  const headers = rows[0].map(mapHeader);
  const out = [];
  for (let r = 1; r < rows.length; r += 1) {
    const obj = {};
    for (let c = 0; c < headers.length; c += 1) {
      const key = headers[c];
      if (!key) continue;
      const val = (rows[r][c] || "").trim();
      if (val) obj[key] = val;
    }
    if (obj.name) out.push(obj);
    if (out.length >= 5000) break;
  }
  return out;
}
